import React, { Component } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { getStripe } from "../../../utils/stripe";
import ProcessPayment from "./components/ProcessPayment";
import { Quotation } from "../../../stores/CoursePreviewStore/Types";
import {
  handleGiftPaymentSuccess,
  handleNonGiftPaymentSuccess,
} from "./service";
import { UserDetail, CompanyDetail } from "../BillingInfo/Types";
import { scrollIntoView } from "../../../utils/helpers";
import { PaymentType, Priceable } from "../Types";

const stripeStyles = {
  base: {
    fontSize: "16px",
    color: "#32325d",
    lineHeight: "40px",
    fontFamily: '"Averta CY", sans-serif',
    fontSmoothing: "antialiased",
    fontWeight: 400,
    "::placeholder": {
      color: "#CFD7E0",
    },
  },
  invalid: {
    color: "#fa755a",
    iconColor: "#fa755a",
  },
};

interface Props {
  kind: string;
  handleChange: (key: string, value: any) => void;
  token?: string;
  paymentType: PaymentType;
  slug: string;
  couponCode: string;
  refCode: string;
  monthlySubsequentPayment: string;
  activeQuote: Quotation;
  priceable: Priceable;
  userDetails: UserDetail;
  companyDetails: CompanyDetail;
  billingType: "user" | "company";
  isCurrentUserPresent: boolean;
  monthlyTotalAmount: string;
  applyVoucher: (couponCode: string, refCode: string) => Promise<void>;
}

interface State {
  isExpanded: boolean;
  userDetails: {
    name: string;
    email: string;
  };
}

class CardDetails extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isExpanded: false,
      userDetails: {
        name: "",
        email: "",
      },
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.userDetails !== prevProps.userDetails) {
      this.setState({ userDetails: this.props.userDetails });
    }
  }

  toggleView = (e) => {
    e.preventDefault();

    this.setState({ isExpanded: !this.state.isExpanded });
  };

  next = (e) => {
    e.preventDefault();
  };

  back = (e) => {
    e.preventDefault();

    const { activeQuote } = this.props;

    this.setState({ isExpanded: false });

    const element = document.getElementById("billing-info");
    scrollIntoView(element);

    const step = activeQuote.amount === 0 ? "plan" : "billing";

    (window as any)?.dataLayer.push({
      event: "customClick",
      elementId: "checkout-payment-back",
    });

    this.props.handleChange("step", step);
  };

  handlePaymentSuccess = (data) => {
    switch (this.props.kind) {
      case "PassGift":
      case "CourseGift":
        handleGiftPaymentSuccess(data, this.props.token);
        break;
      default:
        handleNonGiftPaymentSuccess(data);
        break;
    }
  };

  payUrl = () => {
    switch (this.props.kind) {
      case "PassGift":
      case "CourseGift":
        // api/gift_controller#update 🤷‍♀️
        return `/api/gift/${this.props.token}/pay`;
      default:
        // api/payment_controller#quote 🤷‍♀️
        return `/api/quote/${this.props.activeQuote?.id}/pay`;
    }
  };

  params = () => {
    const { billingType, userDetails, companyDetails } = this.props;

    switch (billingType) {
      case "user":
        return {
          billingEmail: userDetails.email,
          billingName: userDetails.name,
          address: userDetails.address,
          city: userDetails.city,
          postalCode: userDetails.postalCode,
          country: userDetails.country,
          vatNumber: "",
          vatType: "",
          companyName: "",
        };
        break;

      case "company":
        return {
          billingEmail: userDetails.email,
          billingName: companyDetails.billingName || userDetails.name,
          address: companyDetails.address,
          city: companyDetails.city,
          postalCode: userDetails.postalCode,
          country: userDetails.country,
          vatNumber: companyDetails.vatNumber,
          vatType: companyDetails.vatType,
          // Only a company name typed in makes it a company invoice: the
          // buyer's own name above is just the fallback for the card.
          companyName: (companyDetails.billingName || "").trim(),
        };
        break;
    }
  };

  updateUserDetails = (key, value) => {
    const { userDetails } = this.state;

    const updatedUserDetails = { ...userDetails, [key]: value };

    this.setState({ userDetails: updatedUserDetails });
    this.props.handleChange("userDetails", updatedUserDetails);
  };

  render() {
    const {
      activeQuote,
      applyVoucher,
      couponCode,
      isCurrentUserPresent,
      paymentType,
      priceable,
      refCode,
      slug,
      monthlySubsequentPayment,
      monthlyTotalAmount,
    } = this.props;
    const { userDetails } = this.state;
    const elementsOptions =
      activeQuote?.amount > 0
        ? {
            mode: "payment" as "payment",
            amount: activeQuote.fractional,
            currency: activeQuote.currency_code?.toLowerCase(),
          }
        : undefined;

    return (
      <div className="form-section">
        <h2 className="form-section-title">Payment</h2>
        <Elements
          stripe={getStripe()}
          options={elementsOptions}
          key={`${activeQuote?.id}-${activeQuote?.fractional}-${activeQuote?.currency_code}`}
        >
          <ProcessPayment
            isCurrentUserPresent={isCurrentUserPresent}
            userDetails={userDetails}
            payUrl={this.payUrl()}
            activeQuote={activeQuote}
            applyVoucher={applyVoucher}
            couponCode={couponCode}
            paymentType={paymentType}
            priceable={priceable}
            refCode={refCode}
            slug={slug}
            previousAction={this.back}
            monthlySubsequentPayment={monthlySubsequentPayment}
            monthlyTotalAmount={monthlyTotalAmount}
            params={this.params()}
            updateUserDetails={this.updateUserDetails}
            handlePaymentSuccess={this.handlePaymentSuccess}
          />
        </Elements>
      </div>
    );
  }
}

export default CardDetails;
